/* Public verification page for attestation PDFs.
 * Path: /verify/attestation/:token
 * Hit by anyone scanning the QR code on an attestation — no auth.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, XCircle, Buildings } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyAttestation() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/attestation/verify/${token}`);
        setState({ loading: false, data, error: '' });
      } catch (e) {
        setState({
          loading: false,
          data: null,
          error: e.response?.status === 404
            ? 'Attestation introuvable. Ce document n\'a pas été émis par l\'établissement.'
            : 'Erreur de vérification. Réessayez plus tard.',
        });
      }
    };
    load();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 py-8" data-testid="verify-attestation-page">
      <div className="max-w-lg w-full bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8">
        <div className="flex items-center gap-2 mb-4 text-[#0052FF]">
          <Buildings size={20} weight="duotone" />
          <span className="text-xs uppercase tracking-wide font-bold">Vérification officielle</span>
        </div>
        {state.loading ? (
          <p className="text-sm text-[#94A3B8] py-6 text-center">Vérification en cours…</p>
        ) : state.error ? (
          <div className="text-center py-4" data-testid="verify-attestation-error">
            <XCircle size={48} className="mx-auto text-red-500 mb-3" weight="duotone" />
            <h1 className="text-xl font-outfit font-bold text-red-700 mb-1">Document non reconnu</h1>
            <p className="text-sm text-[#64748B]">{state.error}</p>
          </div>
        ) : state.data ? (
          <div data-testid="verify-attestation-success">
            <div className="text-center pb-4 border-b border-[#E2E8F0]">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-2">
                <ShieldCheck size={40} weight="fill" />
              </div>
              <h1 className="text-2xl font-outfit font-bold text-[#10B981]">Attestation authentique</h1>
              <p className="text-xs text-[#64748B] mt-1">
                Ce document a bien été émis par {state.data.school_name || 'l\'établissement'}.
              </p>
            </div>
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Élève</dt>
                <dd className="font-semibold text-right" data-testid="verify-attestation-student">{state.data.student_name || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">N° Matricule</dt>
                <dd className="font-mono text-right" data-testid="verify-attestation-matricule">{state.data.matricule || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Classe</dt>
                <dd className="font-mono text-right">{state.data.class_name || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Année scolaire</dt>
                <dd className="text-right">{state.data.school_year || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Établissement</dt>
                <dd className="font-semibold text-right">{state.data.school_name || '—'}</dd>
              </div>
            </dl>
            <p className="mt-6 text-center text-[10px] text-[#94A3B8]">
              Vérification d&apos;authenticité générée électroniquement.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
